import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Users, Plus, Upload, Search, Filter, ChevronRight,
  CheckCircle2, Clock, XCircle, AlertCircle, Building2,
  Briefcase, UserCheck, Phone, Mail,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const STATUS_CONFIG = {
  alta:        { label: "Alta",       color: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: CheckCircle2 },
  vacaciones:  { label: "Vacaciones", color: "bg-blue-100 text-blue-800 border-blue-200",    icon: Clock },
  incapacidad: { label: "Incapacidad",color: "bg-amber-100 text-amber-800 border-amber-200", icon: AlertCircle },
  baja:        { label: "Baja",       color: "bg-red-100 text-red-800 border-red-200",        icon: XCircle },
};

const POSITION_COLORS = {
  GTE00: "bg-purple-100 text-purple-800",
  SG01:  "bg-indigo-100 text-indigo-800",
  ENT01: "bg-teal-100 text-teal-800",
  CJA01: "bg-orange-100 text-orange-800",
  COC01: "bg-pink-100 text-pink-800",
  EG01:  "bg-slate-100 text-slate-700",
  ADM01: "bg-cyan-100 text-cyan-800",
};

export default function OpsRRHHEmpleados() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [employees, setEmployees] = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState("");
  const [statusFilter, setStatus] = useState("");
  const [positionFilter, setPos]  = useState("");
  const [page, setPage]           = useState(1);
  const [branches, setBranches]   = useState([]);
  const [branchFilter, setBranch] = useState("");

  const limit = 40;

  useEffect(() => {
    api.get("/ops/branches").then(r => setBranches(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (search)        params.set("search", search);
      if (statusFilter)  params.set("status", statusFilter);
      if (positionFilter) params.set("position_code", positionFilter);
      if (branchFilter)  params.set("branch_id", branchFilter);
      const r = await api.get(`/ops/employees?${params}`);
      setEmployees(r.data.employees || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error("Error al cargar colaboradores");
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, positionFilter, branchFilter]);

  useEffect(() => { load(); }, [load]);

  const isHR = ["hr_admin", "corporate_admin"].includes(user?.role);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-6 h-6 text-[#FFDF00]" />
            Colaboradores
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {total} colaborador{total !== 1 ? "es" : ""} en total
          </p>
        </div>
        {isHR && (
          <div className="flex gap-2">
            <Button
              data-testid="import-employees-btn"
              variant="outline"
              size="sm"
              onClick={() => navigate("/ops/rrhh/importar")}
              className="gap-1.5"
            >
              <Upload className="w-4 h-4" />
              Importar Excel
            </Button>
            <Button
              data-testid="new-employee-btn"
              size="sm"
              onClick={() => navigate("/ops/rrhh/empleados/nuevo")}
              className="gap-1.5 bg-slate-900 hover:bg-slate-800 text-white"
            >
              <Plus className="w-4 h-4" />
              Nuevo colaborador
            </Button>
          </div>
        )}
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="employee-search"
            placeholder="Buscar por nombre, RFC, CURP..."
            className="pl-9 h-9"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
        </div>

        <select
          data-testid="status-filter"
          className="h-9 text-sm border border-slate-200 rounded-md px-3 bg-white"
          value={statusFilter}
          onChange={e => { setStatus(e.target.value); setPage(1); }}
        >
          <option value="">Todos los estados</option>
          <option value="alta">Alta</option>
          <option value="vacaciones">Vacaciones</option>
          <option value="incapacidad">Incapacidad</option>
          <option value="baja">Baja</option>
        </select>

        <select
          data-testid="position-filter"
          className="h-9 text-sm border border-slate-200 rounded-md px-3 bg-white"
          value={positionFilter}
          onChange={e => { setPos(e.target.value); setPage(1); }}
        >
          <option value="">Todos los puestos</option>
          <option value="GTE00">Gerente</option>
          <option value="SG01">Sub Gerente</option>
          <option value="ENT01">Entrenador</option>
          <option value="CJA01">Cajero</option>
          <option value="COC01">Cocina</option>
          <option value="EG01">Empleado General</option>
        </select>

        {branches.length > 0 && (
          <select
            data-testid="branch-filter"
            className="h-9 text-sm border border-slate-200 rounded-md px-3 bg-white"
            value={branchFilter}
            onChange={e => { setBranch(e.target.value); setPage(1); }}
          >
            <option value="">Todas las sucursales</option>
            {branches.map(b => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        )}
      </div>

      {/* Tabla */}
      {loading ? (
        <div className="text-center py-16 text-slate-400">Cargando colaboradores...</div>
      ) : employees.length === 0 ? (
        <div className="text-center py-16" data-testid="empty-employees">
          <Users className="w-12 h-12 text-slate-200 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">Sin colaboradores</p>
          <p className="text-sm text-slate-400 mt-1">
            {search ? "Ningún resultado para tu búsqueda" : "Usa el botón de importar para cargar desde Excel"}
          </p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm" data-testid="employees-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-3 font-medium text-slate-600">Colaborador</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Puesto</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600 hidden md:table-cell">Sucursal</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600 hidden lg:table-cell">Contacto</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Estado</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {employees.map(emp => {
                const st = STATUS_CONFIG[emp.status] || STATUS_CONFIG.alta;
                const Icon = st.icon;
                const posColor = POSITION_COLORS[emp.position_code] || "bg-slate-100 text-slate-700";
                return (
                  <tr
                    key={emp.id}
                    className="hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/ops/rrhh/empleados/${emp.id}`)}
                    data-testid={`employee-row-${emp.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center font-semibold text-xs shrink-0">
                          {emp.first_name?.[0]}{emp.last_name?.[0]}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">
                            {emp.first_name} {emp.last_name}
                          </p>
                          <p className="text-xs text-slate-400">{emp.employee_number}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${posColor}`}>
                        {emp.position}
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <div className="flex items-center gap-1 text-slate-600">
                        <Building2 className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate max-w-[150px]">{emp.branch_name || "—"}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <div className="text-slate-500 space-y-0.5">
                        {emp.phone && (
                          <div className="flex items-center gap-1 text-xs">
                            <Phone className="w-3 h-3" />{emp.phone}
                          </div>
                        )}
                        {emp.personal_email && (
                          <div className="flex items-center gap-1 text-xs">
                            <Mail className="w-3 h-3" />
                            <span className="truncate max-w-[140px]">{emp.personal_email}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${st.color}`}>
                        <Icon className="w-3 h-3" />
                        {st.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <ChevronRight className="w-4 h-4 text-slate-300" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Paginación */}
          {total > limit && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50 text-sm text-slate-600">
              <span>{(page - 1) * limit + 1}–{Math.min(page * limit, total)} de {total}</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                  Anterior
                </Button>
                <Button variant="outline" size="sm" disabled={page * limit >= total} onClick={() => setPage(p => p + 1)}>
                  Siguiente
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
